import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./CustomDatePicker.css";
import { TextField, IconButton, Box, Tooltip } from "@mui/material";
import { CalendarToday, Clear } from "@mui/icons-material";
import { handleKeyDownIfEnter } from "../../../utils/hooks/eventMonitor";
import { parse, isValid, format } from "date-fns";

const CustomDatePicker = ({
  selected,
  onChange,
  label,
  disabled = false,
  dateFormat = "dd-MM-yyyy",
  showYearPicker = false,
  maxDate,
  minDate,
  onKeyDown,
  data,
  index,
  handleAmountFocus,
  fullWidth = true,
  size = "small",
  allowManualInput = true,
  clearable = true,
  placeholder,
  helperText,
  error = false,
  labelSx,
  showAdornments = true,
  inputRef,
  ariaLabel,
  ...props
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Update input value when selected date changes
  useEffect(() => {
    if (selected) {
      setInputValue(format(selected, dateFormat));
    } else {
      setInputValue("");
    }
  }, [selected, dateFormat]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Try to parse the input value
    const parsedDate = parse(value, dateFormat, new Date());

    // Only trigger onChange if the date is valid
    if (isValid(parsedDate)) {
      // Check if date is within min/max constraints
      const isWithinRange =
        (!minDate || parsedDate >= minDate) &&
        (!maxDate || parsedDate <= maxDate);

      if (isWithinRange) {
        onChange(parsedDate);
      }
    }
  };

  const handleInputBlur = () => {
    // Reset to selected date format on blur if input is invalid
    if (selected) {
      setInputValue(format(selected, dateFormat));
    } else {
      setInputValue("");
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setInputValue("");
  };

  const handleCalendarClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <Box
      className='custom-datepicker-wrapper'
      sx={{
        width: fullWidth ? "100%" : "auto",
      }}
    >
      <DatePicker
        selected={selected}
        onChange={onChange}
        dateFormat={dateFormat}
        showYearPicker={showYearPicker}
        maxDate={maxDate}
        minDate={minDate}
        disabled={disabled}
        open={isOpen}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => setIsOpen(false)}
        popperPlacement='bottom'
        showMonthDropdown
        showYearDropdown
        placeholderText={placeholder}
        customInput={
          <TextField
            inputRef={inputRef}
            fullWidth={fullWidth}
            label={label}
            value={inputValue}
            onChange={allowManualInput ? handleInputChange : undefined}
            onBlur={allowManualInput ? handleInputBlur : undefined}
            onClick={handleCalendarClick}
            size={size}
            autoComplete='off'
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            inputProps={{
              autoComplete: "off",
              autoCorrect: "off",
              autoCapitalize: "off",
              spellCheck: "false",
              "aria-label": ariaLabel || label || "Date picker",
            }}
            InputProps={{
              readOnly: !allowManualInput,
              endAdornment: showAdornments ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {clearable && selected && !disabled && (
                    <Tooltip title='Clear date'>
                      <IconButton
                        size='small'
                        onClick={handleClear}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                            backgroundColor: "error.light",
                          },
                        }}
                      >
                        <Clear fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title='Open calendar'>
                    <IconButton
                      size='small'
                      onClick={handleCalendarClick}
                      disabled={disabled}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "primary.main",
                          backgroundColor: "primary.light",
                        },
                        "&:disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <CalendarToday fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : undefined,
              sx: {
                cursor: disabled ? "not-allowed" : "text",
                "&:hover": {
                  "& fieldset": {
                    borderColor: disabled ? "text.disabled" : "primary.main",
                  },
                },
              },
            }}
            onKeyDown={(e) => {
              if (onKeyDown) {
                onKeyDown(e);
              } else if (data && index !== undefined && handleAmountFocus) {
                handleKeyDownIfEnter(e, data, index, handleAmountFocus);
              }
              // Close calendar on Escape
              if (e.key === "Escape") {
                setIsOpen(false);
              }
              // Open calendar on Space or Enter
              if ((e.key === " " || e.key === "Enter") && !disabled) {
                e.preventDefault();
                setIsOpen(true);
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.paper",
              },
              ...props.sx,
            }}
          />
        }
        {...props}
      />
    </Box>
  );
};

export default CustomDatePicker;
